import React, { useState, useEffect, useMemo } from 'react'
import { Grid, Paper } from '@material-ui/core'
import { useTranslation } from 'react-i18next'

import { Loader, ErrorView, FieldCheckBox } from '@percent/admin-dashboard/common/components'
import { EligibilityDetailsViewProps } from './EligibilityDetailsView.types'
import styles from './EligibilityDetailsView.module.scss'
import { OrganisationTypes } from '@percent/domain/verifications'
import { ActivityEventSubTag } from '@percent/admin-dashboard/api/types'
import { OrganisationDetails } from './components/OrganisationDetails/OrganisationDetails'
import { Badge, Flex } from '@percent/lemonade'
import { useActivityTagsState } from '@percent/admin-dashboard/common/hooks'
import { ActivityTags } from '@percent/admin-dashboard/app/eligibility/eligibilityDetail/EligibilityDetailsView/components/ActivityTags/ActivityTags'

export function EligibilityDetailsView({
  errorMessage,
  isLoading,
  organisation,
  activityEvent
}: Readonly<EligibilityDetailsViewProps>) {
  const { t } = useTranslation()
  const { activityTags } = useActivityTagsState()
  const [subTagsForm, setSubTagsForm] = useState<ActivityEventSubTag[]>(
    activityEvent?.subTags?.map(subTag => ({ id: subTag, meetsCriteria: true })) ?? []
  )
  const [isFlagged, setIsFlagged] = useState(false)
  const [isNoneOfTheAbove, setIsNoneOfTheAbove] = useState(false)

  useEffect(() => {
    if (activityEvent?.subTags === null) {
      setIsFlagged(true)
    }
  }, [activityEvent])

  useEffect(() => {
    if (activityEvent) {
      setSubTagsForm(activityEvent?.subTags?.map(subTag => ({ id: subTag, meetsCriteria: true })) ?? [])
    }
  }, [activityEvent])

  const onSubTagChange = (subTagId: string) => {
    const subTagExists = subTagsForm.findIndex(subTag => subTag.id === subTagId) !== -1

    if (subTagExists) {
      setSubTagsForm(prevState => prevState?.filter(el => el.id !== subTagId))
    } else {
      setSubTagsForm(prevState => {
        if (prevState) {
          return [...prevState, { id: subTagId, meetsCriteria: true }]
        }

        return prevState
      })
    }
  }

  const onEducationSubTagChange = (educationSubTags: ActivityEventSubTag[], subTagId?: string) => {
    setSubTagsForm(
      educationSubTags.map(subTag => ({
        id: subTag.id,
        meetsCriteria: subTag.id === subTagId ? !subTag.meetsCriteria : subTag.meetsCriteria
      }))
    )
  }

  const setNotDeprecatedSubTags = (notDeprecatedSubTags: ActivityEventSubTag[]) => {
    setSubTagsForm(notDeprecatedSubTags)
  }

  const handleFlagOrg = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsFlagged(event.target.checked)
    setSubTagsForm([])
  }

  const activeSubTags = useMemo(() => {
    const availableTags = activityTags
      ?.filter(tag => tag.subTags.map(subTag => subTag.id).some(subTagId => activityEvent?.subTags?.includes(subTagId)))
      ?.flatMap(tag => tag.subTags)

    const tags = activityEvent?.subTags?.map(subTag => ({ id: subTag, meetsCriteria: true })) ?? []

    return availableTags?.reduce<Array<{ name: string; id: string }>>((accTagNames, currTag) => {
      const subTag = tags.find(tag => tag.id === currTag.id)

      if (subTag) {
        accTagNames.push({ name: currTag.name, id: currTag.id })
      }

      return accTagNames
    }, [])
  }, [activityEvent?.subTags, activityTags])

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  if (isLoading) {
    return <Loader />
  }

  const isOnlyEducationOrganisation = organisation?.type === OrganisationTypes.Education

  const hasPreviousSubTags = !!activityEvent?.subTags

  return (
    <>
      <OrganisationDetails
        organisation={organisation}
        subTagsForm={subTagsForm}
        errorMessage={errorMessage}
        isLoading={isLoading}
        isFlagged={isFlagged}
        isNoneOfTheAbove={isNoneOfTheAbove}
      />
      <Grid container spacing={0} component={Paper} elevation={0} className={styles.flagWrapper}>
        <span className={styles.headerText}>{t('typography.insufficientInformation')}</span>
        <FieldCheckBox
          onChange={handleFlagOrg}
          active={isFlagged}
          name="sanction"
          disabled={isNoneOfTheAbove || hasPreviousSubTags}
        >
          <span className={styles.checkboxLabel}>{t('typography.notEnoughInfo')}</span>
        </FieldCheckBox>
      </Grid>

      {!!activeSubTags?.length && (
        <Grid container spacing={0} component={Paper} elevation={0} className={styles.activeSubTagsWrapper}>
          <span className={styles.headerText}>{t('typography.activeSubTags')}</span>
          <Flex gap={8}>
            <span className={styles.descriptionText}>{t('typography.activity')}</span>
            <Flex gap={8} align="center" wrap="wrap" data-testid="active-subtags-list">
              {activeSubTags?.map(subTag => (
                <Badge key={subTag.id} variant="informative" icon="categories">
                  {subTag.name}
                </Badge>
              ))}
            </Flex>
          </Flex>
        </Grid>
      )}

      <Grid container spacing={0} component={Paper} elevation={0} className={styles.algoliaContainer}>
        <ActivityTags
          subTagsForm={subTagsForm}
          onSubTagChange={onSubTagChange}
          isFlagged={isFlagged}
          isOnlyEducationOrganisation={isOnlyEducationOrganisation}
          setNotDeprecatedSubTags={setNotDeprecatedSubTags}
          onEducationSubTagChange={onEducationSubTagChange}
          isNoneOfTheAbove={isNoneOfTheAbove}
          setIsNoneOfTheAbove={setIsNoneOfTheAbove}
        />
      </Grid>
    </>
  )
}
